import React from 'react';
import { View, TextInput, Image, TouchableOpacity, Text, ImageSourcePropType } from 'react-native';
import { styles } from './IconInput.styles';
import { themes } from '../../theme/theme';
import InputError from 'src/components/input/InputError';

interface IconInputProps {
  value: string;
  onPress?: () => void; // Callback when the entire field is clicked
  onIconPress?: () => void; // Callback when the icon is clicked
  placeholder?: string;
  iconSource: ImageSourcePropType;
  iconWidth: number;
  iconHeight: number;
  label: string;
  required?: boolean;
  disabled?: boolean;
  errorText?: string;
  isError?: boolean;
  testID?: string;
}

// Ensure at least one of `onPress` or `onIconPress` is provided
const assertProps = (onPress?: () => void, onIconPress?: () => void) => {
  if (!onPress && !onIconPress) {
    throw new Error('You must pass either `onPress` or `onIconPress` prop to IconInput.');
  }
};

const IconInput: React.FC<IconInputProps> = ({
  value,
  onPress,
  onIconPress,
  placeholder,
  iconSource,
  iconWidth,
  iconHeight,
  label,
  required = false,
  disabled = false,
  errorText = '',
  isError = false,
  testID,
}) => {
  assertProps(onPress, onIconPress); // Validate the props

  const inputColor = value.length > 0 ? themes.light.gray_blue[1] : themes.light.gray_blue[4];
  const borderColor = isError ? themes.light.red[3] : themes.light.gray_blue[6];
  const labelColor = isError ? themes.light.red[3] : themes.light.gray_blue[1];
  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.8} disabled={disabled} testID={testID}>
      <View>
        <View style={styles.labelContainer}>
          <Text style={{ ...styles.label, color: labelColor }}>{label}</Text>
          {required && <Text style={styles.requiredIndicator}>*</Text>}
        </View>
        <View
          style={[
            styles.inputContainer,
            disabled && styles.disabledInputContainer,
            { borderColor: borderColor },
          ]}>
          <TextInput
            style={[styles.input, { color: inputColor }]}
            value={value}
            placeholder={placeholder}
            editable={false}
            pointerEvents="none"
          />
          <TouchableOpacity
            onPress={() => (onIconPress ? onIconPress() : onPress ? onPress() : {})}
            style={styles.iconContainer}>
            <Image
              source={iconSource}
              style={{ width: iconWidth, height: iconHeight }}
              resizeMode="contain"
            />
          </TouchableOpacity>
        </View>
      </View>
      {isError && <InputError errorText={errorText} />}
    </TouchableOpacity>
  );
};

export default IconInput;
